import {Cache, CanvasSource, DOMAdapter, Rectangle, Sprite, Texture} from 'pixi.js';
import {MaxRectsPacker} from 'maxrects-packer';
import {DAMAGE_PARTS} from './remoteDamage.js';
import {WOUND_FLAGS, WOUND_LIMITS, WOUND_TEXTURES, woundAngle} from './woundProtocol.js';
import {getCharacterBloodColors} from '../utils/BloodColors.js';
import {neutralBloodTexture} from '../utils/bloodTexture.js';
import {DEFAULT_BLOOD_COLOR, rainbowBloodColor} from '../../static/mod/bloodColors.js';

export const remoteWoundBudget = {
	atlases: 0,
	beginFrame() { this.atlases = WOUND_LIMITS.atlasesPerFrame; },
};

const drawTexture = (ctx, texture, x, y) => {
	const {frame, source, trim} = texture;
	ctx.drawImage(source.resource, frame.x, frame.y, frame.width, frame.height,
		x + (trim?.x || 0), y + (trim?.y || 0), frame.width, frame.height);
};

// One small, lazily-created atlas per damaged peer. Only changed atlases upload
// (at most two per frame). Canvas source-atop/destination-out clips stamps to
// existing skin alpha without per-sprite filters, GPU readbacks or physics.
export class RemoteSkinDamage {
	constructor(owner) {
		this.owner = owner;
		this.parts = new Map();
		this.enabled = false;
	}

	prepare() {
		const pack = new MaxRectsPacker(WOUND_LIMITS.atlasSize, WOUND_LIMITS.atlasSize, 2, {pot: false, allowRotation: false});
		pack.addArray(DAMAGE_PARTS.map((key, part) => {
			const sprite = this.owner.sprites[key];
			return {width: sprite.texture.width, height: sprite.texture.height, key, part, base: sprite.texture};
		}));
		const bin = pack.bins[0];
		if (pack.bins.length !== 1 || !bin) return false;
		this.canvas = DOMAdapter.get().createCanvas(bin.width, bin.height);
		this.canvas.width = bin.width;
		this.canvas.height = bin.height;
		this.context = this.canvas.getContext('2d');
		this.atlas = new Texture({source: new CanvasSource({resource: this.canvas, resolution: 1})});
		for (const rect of bin.rects) {
			drawTexture(this.context, rect.base, rect.x, rect.y);
			const texture = new Texture({source: this.atlas.source, frame: new Rectangle(rect.x, rect.y, rect.width, rect.height)});
			this.parts.set(rect.part, {...rect, texture, wounded: false});
		}
		return true;
	}

	setEnabled(enabled) {
		if (enabled === this.enabled) return;
		this.enabled = enabled;
		for (const part of this.parts.values()) {
			const sprite = this.owner.sprites[part.key];
			sprite.rippleDamageVisible = enabled;
			sprite.texture = enabled && part.wounded ? part.texture : part.base;
			if (sprite.flesh) sprite.flesh.visible = enabled && sprite.visible;
		}
	}

	stamp(wound) {
		const part = this.parts.get(wound.part);
		const name = WOUND_TEXTURES[wound.texture];
		if (!part || !Cache.has(name)) return;
		const color = this.rainbow ? rainbowBloodColor(this.time / 1000) :
			(this.owner.bloodColors || getCharacterBloodColors(this.owner.skin)).start;
		const recolor = name === 'Decal.png' && color !== DEFAULT_BLOOD_COLOR;
		const texture = Texture.from(name);
		const ctx = this.context;
		const scale = wound.size / 32;
		ctx.save();
		ctx.beginPath();
		ctx.rect(part.x, part.y, part.width, part.height);
		ctx.clip();
		ctx.translate(part.x + wound.x / 4, part.y + wound.y / 4);
		ctx.rotate(woundAngle(wound.rotation));
		ctx.scale(scale, scale);
		ctx.globalCompositeOperation = 'source-atop';
		if (recolor) {
			const neutral = neutralBloodTexture(texture);
			// Tint the stamp on a small scratch canvas, keeping the wound atlas's
			// source-atop mask and later carving operation unchanged.
			this.stampCanvas ||= DOMAdapter.get().createCanvas(texture.orig.width, texture.orig.height);
			this.stampCanvas.width = texture.orig.width;
			this.stampCanvas.height = texture.orig.height;
			const stamp = this.stampCanvas.getContext('2d');
			drawTexture(stamp, neutral, 0, 0);
			stamp.globalCompositeOperation = 'source-in';
			stamp.fillStyle = color;
			stamp.fillRect(0, 0, this.stampCanvas.width, this.stampCanvas.height);
			ctx.drawImage(this.stampCanvas, -texture.orig.width / 2, -texture.orig.height / 2);
		} else drawTexture(ctx, texture, -texture.orig.width / 2, -texture.orig.height / 2);
		if (wound.flags & WOUND_FLAGS.CARVE) {
			ctx.globalCompositeOperation = 'destination-out';
			ctx.scale(0.6, 0.6);
			drawTexture(ctx, texture, -texture.orig.width / 2, -texture.orig.height / 2);
		}
		ctx.restore();
		part.wounded = true;
		const sprite = this.owner.sprites[part.key];
		if (!sprite.flesh) {
			const baseName = part.key === 'head' ? 'Normal_Head' : part.base.label?.replace(/\d{4}$/, '');
			const fleshName = `${baseName}_Flesh0000`;
			if (Cache.has(fleshName)) {
				const flesh = new Sprite(Texture.from(fleshName));
				flesh.anchor.set(sprite.anchor.x * part.width / flesh.texture.width, sprite.anchor.y * part.height / flesh.texture.height);
				sprite.parent.addChildAt(flesh, sprite.parent.getChildIndex(sprite));
				sprite.flesh = flesh;
				flesh.position.copyFrom(sprite.position);
				flesh.rotation = sprite.rotation;
				flesh.scale.copyFrom(sprite.scale);
				flesh.visible = sprite.visible && this.enabled;
			}
		}
		if (sprite.flesh && wound.burn) {
			sprite.flesh.burn = Math.min(1, (sprite.flesh.burn || 0) + wound.burn / 255);
			const color = Math.round(255 * (1 - sprite.flesh.burn));
			sprite.flesh.tint = color * 0x010101;
		}
		if (this.enabled) sprite.texture = part.texture;
	}

	update(now, settings, effects) {
		this.time = now;
		this.rainbow = !settings.bloodEnabled;
		const owner = this.owner;
		const wounds = owner.wounds;
		const ready = !!settings.goreEnabled && wounds.epoch === owner.poseEpoch && !!owner.sprites;
		this.setEnabled(ready);
		if (!ready) return;
		// Old events after a hidden tab / slow skin decode may stamp the skin,
		// but should never replay a burst of historical blood sprays.
		for (const event of wounds.sprays) if (now - event.time <= 450) effects.spray(owner, event.wound);
		wounds.sprays.length = 0;
		if (!wounds.stamps.length || remoteWoundBudget.atlases <= 0) return;
		// Keep persistent commands while offscreen. No invisible texture uploads.
		if (!effects.isVisible(owner.state.body.x, owner.state.body.y)) return;
		remoteWoundBudget.atlases--;
		if (!this.atlas && !this.prepare()) return;
		for (let i = 0; i < WOUND_LIMITS.stampsPerFrame && wounds.stamps.length; i++) this.stamp(wounds.stamps.shift());
		this.atlas.source.update();
	}

	clear() {
		for (const part of this.parts.values()) {
			const sprite = this.owner.sprites?.[part.key];
			if (sprite && !sprite.destroyed) {
				sprite.texture = part.base;
				sprite.flesh?.destroy();
				sprite.flesh = null;
				delete sprite.rippleDamageVisible;
			}
			part.texture.destroy(false);
		}
		this.parts.clear();
		this.atlas?.destroy(true);
		this.atlas = null;
		if (this.canvas) this.canvas.width = this.canvas.height = 1;
		this.canvas = this.context = null;
		this.stampCanvas = null;
		this.enabled = false;
	}
}
